import { openDB } from "idb";

import type { IDBPDatabase } from "idb";

export interface IDBManagerInit {
  name: string;
}

export interface StoreObject<T = string> {
  expires: number;
  value: T;
}

const DATABASE_NAME = "bp_web_components";
const DATABASE_VERSION = 1;
const EXPIRES_INDEX_NAME = "expires";
const MAX_RETRY_COUNT = 1;

/**
 * Creates a managed IndexedDB connection.
 * @class
 */
export class IDBManager {
  /**
   * The object store name.
   */
  name: string;

  /**
   * A reference to the awaited database connection.
   */
  connection: Promise<IDBPDatabase> | null = null;

  constructor(init: IDBManagerInit) {
    this.name = init.name;
  }

  /**
   * Opens a connection to an IndexedDB database and returns
   * an object store.
   */
  async getDatabase() {
    if (!this.connection) {
      this.connection = openDB(DATABASE_NAME, DATABASE_VERSION, {
        upgrade: (db) => {
          const store = db.createObjectStore(this.name);

          // Create an index on the `expires` data
          store.createIndex(EXPIRES_INDEX_NAME, EXPIRES_INDEX_NAME);
        },
      });
    }

    return this.connection.then((db) => this.cleanUp(db));
  }

  /**
   * Removes expired items from the store.
   */
  async cleanUp(db: IDBPDatabase) {
    // Create a range that includes anything in the past
    const range = IDBKeyRange.upperBound(Date.now());

    // Select expired items
    let cursor = await db
      .transaction(this.name, "readwrite")
      .store.index(EXPIRES_INDEX_NAME)
      .openCursor(range);

    // Iterate and delete
    while (cursor) {
      await cursor.delete();
      cursor = await cursor.continue();
    }

    return db;
  }

  /**
   * Attempts to perform an IndexedDB transaction. Retries if
   * the transaction fails, usually because the connection was closed.
   * @see https://bugs.webkit.org/show_bug.cgi?id=197050
   */
  async attemptTransaction<T>(tx: (db: IDBPDatabase) => Promise<T>) {
    let retries = 0;

    const attempt = async (
      resolve: (value: T) => T | void,
      reject: (reason?: any) => void,
    ): Promise<T | void> => {
      return this.getDatabase()
        .then(tx)
        .then(resolve)
        .catch(async (error) => {
          if (++retries > MAX_RETRY_COUNT) {
            reject(error);
            return;
          }

          return this.getDatabase()
            .then((db) => {
              // Ensure the connection is closed before re-attempting
              db.close();
              this.connection = null;

              // Retry
              return attempt(resolve, reject);
            })
            .catch(reject);
        });
    };

    return new Promise(attempt);
  }

  /**
   * Puts an item into the store.
   */
  async put(key: string, data: StoreObject) {
    return this.attemptTransaction((db) => db.put(this.name, data, key));
  }

  /**
   * Retrieves an item from the store.
   */
  async get<T>(key: string) {
    return this.attemptTransaction<StoreObject<T>>((db) =>
      db.get(this.name, key),
    );
  }
}
